import React, { createRef, useState, useCallback, Fragment, useRef } from 'react';
import { Heart, Lock, HelpCircle } from 'react-feather';
import { useHistory, useLocation } from 'react-router-dom';

import { StepWrapperProps, StepWrapper } from './StepWrapper';
import { PlaidWrapper } from './PlaidButton/PlaidButton';

import { TiledButton } from 'components/tiled-button/TiledButton';
import { colors } from 'styles/settings';
import { Routes, getPath } from 'routes';
import { updateEnrollmentProgress } from 'common/api/crm/organization/enrollment';
import { ProgressState } from 'common/api/crm/enums/ProgressState';
import { withRetries } from 'common/utils/withRetries';
import { Organization } from 'common/api/crm/models/Organization';
import { AchVerificationStatus, OrgAchAccount } from 'common/api/e-comm/ach/models/AchAccount';
import { Check } from 'components/custom-icons';
import { Btn } from 'components/btn/Btn';
import { PaperOnlyModal } from 'features/coordinator/components/PaperOnlyModal';
import { LinkBankLaterModal } from 'features/coordinator/components/LinkBankLaterModal';
import { Branding, IS_FLIPGIVE } from 'common/env';
import { createStyles } from 'utils/createStyle';
import { openLoader, LoadingOverlay, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { setBillingSelection, BillingSelection } from 'common/api/e-comm/payment-types';
import { useAlert } from 'modules/alerts';
import { SERVICE_UNAVAILABLE_MESSAGE } from 'config';

interface ListItemConfig {
    icon?: React.ReactNode;
    text: React.ReactNode;
}

interface GetEarningsStepProps extends StepWrapperProps {
    organization: Organization;
}

const styles = createStyles({
    buttonContainer: [
        'flex flex-col items-center',
        'sm:flex-row sm:items-start sm:justify-between',
    ],
    conjunction: [
        'text-brand-dark text-xl font-semibold mb-6 flex items-center',
        'sm:h-44 sm:mr-6', // Match height and margin of tile button without description
    ],
});

export const GetEarningsStep = (props: GetEarningsStepProps) => {
    const buttonContainerRef = createRef<HTMLDivElement>();
    const params = new URLSearchParams(useLocation().search);
    const [isOAuth, setIsOAuth] = useState(Boolean(params.get('oauth_state_id')));
    const [modal, setModal] = useState<'upload-check' | 'paper-only' | 'link-bank-later' | null>(
        null
    );

    const {
        organization: { id: orgId = '' },
    } = props;

    const HeartIcon = (
        <div className="mr-2 -mt-0.5" aria-hidden="true">
            <Heart color={colors.brandAccent} size={32} />
        </div>
    );

    const listItems: ListItemConfig[] = [
        {
            icon: HeartIcon,
            text: (
                <>
                    <strong>More Brands, Fastest Shipping</strong>: Greater earnings with instant
                    access to our full catalog of eGift cards, physical gift cards and reloadable
                    gift cards, with the fastest and most economical shipping available for physical
                    gift card orders
                </>
            ),
        },
        {
            icon: HeartIcon,
            text: (
                <>
                    <strong>Monthly Payouts</strong>: Get your earnings faster with monthly
                    automatic direct deposits, no need to wait for a quarterly{' '}
                    {IS_FLIPGIVE ? 'cheque' : 'check'}
                </>
            ),
        },
        ...(IS_FLIPGIVE
            ? []
            : [
                  {
                      icon: HeartIcon,
                      text: (
                          <>
                              <strong>Lowest Fees, Maximum Earnings</strong>: Participants pay only
                              $0.29 per order when using a bank account for secure payment
                              processing
                          </>
                      ),
                  },
              ]),
    ];

    const history = useHistory();
    const onAchAdded = useCallback(
        async <T,>(payload: T) => {
            withRetries(updateEnrollmentProgress)({
                state: ProgressState.AccountLinkedWithPlaid,
                organizationId: props.organization.id,
            });

            const { verificationStatus } = payload as unknown as OrgAchAccount;

            if (verificationStatus === AchVerificationStatus.PendingManualVerification) {
                history.replace(getPath(Routes.StartProgramCompleted, { status: 'deposits' }));
                return;
            }

            history.replace(getPath(Routes.StartProgramCompleted, {}));
        },
        [history, props.organization.id]
    );

    const loader = useRef<LoadingOverlay>(null);
    const alert = useAlert();
    const onVoidedCheckClick = useCallback(async () => {
        openLoader(loader.current);
        const result = await withRetries(setBillingSelection)(orgId, BillingSelection.VoidedCheck);
        await closeLoader(loader.current);
        if (result.error) {
            alert({ message: SERVICE_UNAVAILABLE_MESSAGE });
            return;
        }
        withRetries(updateEnrollmentProgress)({
            state: ProgressState.VoidedCheckSelected,
            organizationId: orgId,
        });
        history.replace(Routes.StartProgramVoidedCheck);
    }, [alert, history, orgId]);

    const onPaperModalClose = () => setModal(null);
    const onLinkBankLaterModalClose = () => setModal(null);
    const onPrimaryBtnClick = () =>
        history.replace(getPath(Routes.StartProgramCompleted, { status: 'paper-only' }));

    const onAnotherOptionClick = () => {
        setModal(IS_FLIPGIVE ? 'link-bank-later' : 'paper-only');
    };

    return (
        <Fragment>
            <StepWrapper heading={props.heading} subheading={null}>
                <div className="text-dark-blue text-lg font-normal leading-7 space-y-3">
                    <p>
                        Securely link your organization&apos;s bank account below so you can pay for
                        gift card orders and receive earnings via direct deposit.
                    </p>
                    <p>
                        <span className="font-semibold">95% of organizations</span> link a bank
                        account to manage their {Branding} program.
                    </p>
                </div>

                <h2 className={'text-2xl text-brand-dark font-semibold my-6'}>
                    Here&apos;s why we recommend it:
                </h2>

                <ul className={'text-brand-dark text-lg mb-6'}>
                    {listItems.map((listItem, i) => (
                        <li className="flex mb-4" key={i}>
                            <div className="flex-shrink-0 mt-1">{listItem.icon}</div>
                            <span>{listItem.text}</span>
                        </li>
                    ))}
                </ul>

                <div ref={buttonContainerRef} className={styles.buttonContainer}>
                    <TiledButton
                        buttonClassName="justify-around o:w-56"
                        descriptionClassName="o:w-56"
                        buttonText={
                            IS_FLIPGIVE
                                ? 'Send a Voided Cheque or Deposit Slip'
                                : 'Send a Voided Check or Deposit Slip'
                        }
                        bannerText={'Most Popular'}
                        onClick={onVoidedCheckClick}
                        icon={<Check className={'mt-4 text-brand'} width={64} height={64} />}
                        trailingDescription="Email or upload a photo on the next step."
                    />
                    <div className={styles.conjunction}>or</div>
                    <PlaidWrapper
                        orgId={orgId}
                        onSuccess={onAchAdded}
                        isOAuth={isOAuth}
                        onExit={() => {
                            setIsOAuth(false);
                        }}
                    >
                        {(openPlaid, isPlaidReady) => (
                            <TiledButton
                                isSecondary
                                disabled={!isPlaidReady as boolean}
                                buttonClassName="o:w-56"
                                descriptionClassName="o:w-56"
                                buttonText={'Verify banking information online'}
                                onClick={openPlaid}
                                icon={<Lock className={'mt-4 text-brand'} size={64} />}
                                bannerText={'Fastest'}
                                trailingDescription="We use Plaid to connect your account safely, with the same 256-bit encryption banks use. Simply log in to your bank or enter your account info."
                            />
                        )}
                    </PlaidWrapper>
                </div>
            </StepWrapper>

            <hr className="border-grey-5 mb-8" />

            <div className="px-4 mb-12">
                <div className="flex justify-center mb-5 text-grey-2">
                    <div className="flex-shrink-0 mr-2">
                        <HelpCircle />
                    </div>
                    <h2>
                        {IS_FLIPGIVE
                            ? "I don't have my organization's banking information right now."
                            : "Don't have your organization's banking information right now?"}
                    </h2>
                </div>

                <div className="text-center">
                    <Btn isSecondary onClick={onAnotherOptionClick}>
                        Link a Bank Account Later
                    </Btn>
                </div>
            </div>

            <PaperOnlyModal
                orgId={orgId}
                isOpen={modal === 'paper-only'}
                onClose={onPaperModalClose}
                primaryBtnText="Link Bank Account Later"
                onBtnPrimaryClick={onPrimaryBtnClick}
                secondaryBtnText="No, I can link a bank account"
                onBtnSecondaryClick={onPaperModalClose}
            />

            <LinkBankLaterModal
                isOpen={modal === 'link-bank-later'}
                onClose={onLinkBankLaterModalClose}
            />

            <LoadingOverlay ref={loader} />
        </Fragment>
    );
};
